<template>
  <div class="bui-entity-listing-page uptime-redesign">
    <div class="uptime-redesign__inner">
      <!-- Page header -->
      <header class="pe-header">
        <div class="pe-header__left">
          <div class="pe-header__title-row">
            <h1 class="bui-page-title">System Status</h1>
            <button
              type="button"
              class="pe-header__refresh"
              aria-label="Refresh uptime data"
              :disabled="isLoading"
              @click="fetchUptimeData"
            >
              <PhArrowsClockwise :size="22" weight="bold" />
            </button>
          </div>
          <div class="pe-header__subtitle">Player uptime over the selected date range</div>
        </div>
      </header>

      <!-- Summary stat tiles -->
      <section class="pe-stats" aria-label="Uptime summary">
        <StatTile :icon="PhListChecks" label="Total Records" :value="uptimeData.length" />
        <StatTile :icon="PhUsers" label="Unique Players" :value="uniquePlayers" />
        <StatTile
          :icon="PhPulse"
          label="Online"
          :value="onlineCount"
          :tone="onlineCount > 0 ? 'good' : 'default'"
        />
        <StatTile
          :icon="PhPulse"
          label="Offline"
          :value="offlineCount"
          :tone="offlineCount > 0 ? 'bad' : 'default'"
        />
        <StatTile :icon="PhCalendarBlank" label="Date Range" :value="formatDateRange() || '—'" />
      </section>

      <!-- Chart card -->
      <InfoCard flush>
        <template #title>
          <h2 class="info-card__title-text">Uptime Over Time</h2>
        </template>
        <template #actions>
          <div class="uptime-dates">
            <label class="uptime-date">
              <span class="uptime-date__label">Start</span>
              <input
                type="date"
                class="uptime-date__input"
                v-model="startDate"
                :max="endDate"
                @change="fetchUptimeData"
              />
            </label>
            <label class="uptime-date">
              <span class="uptime-date__label">End</span>
              <input
                type="date"
                class="uptime-date__input"
                v-model="endDate"
                :min="startDate"
                @change="fetchUptimeData"
              />
            </label>
          </div>
        </template>

        <div class="uptime-chart-panel">
          <BuiSpinner v-if="isLoading" inline message="Loading uptime data…" />

          <div v-else-if="error" class="uptime-state uptime-state--error">
            <p class="uptime-state__message">{{ error }}</p>
            <button type="button" class="uptime-retry" @click="fetchUptimeData">Retry</button>
          </div>

          <div v-else-if="uptimeData.length === 0" class="uptime-state">
            <p>No uptime data available for the selected date range.</p>
          </div>

          <UptimeChart
            v-else
            :uptime-data="uptimeData"
            :start-date="startDateObj"
            :end-date="endDateObj"
          />
        </div>
      </InfoCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { getPlayerUptimes, type UptimeData } from '@/helpers/api/player/uptimes';
import UptimeChart from '@/components/UptimeChart/UptimeChart.vue';
import StatTile from '@/views/PlayerEditorView/StatTile/StatTile.vue';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
import BuiSpinner from '@/components/bui/atoms/BuiSpinner/BuiSpinner.vue';
import { PhArrowsClockwise, PhListChecks, PhUsers, PhPulse, PhCalendarBlank } from '@phosphor-icons/vue';

const startDate = ref('');
const endDate = ref('');
const uptimeData = ref<UptimeData[]>([]);
const isLoading = ref(false);
const error = ref('');

// Set default date range (last 30 days)
const setDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);

  endDate.value = end.toISOString().split('T')[0];
  startDate.value = start.toISOString().split('T')[0];
};

const startDateObj = computed(() => {
  const [year, month, day] = startDate.value.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
});
const endDateObj = computed(() => {
  const [year, month, day] = endDate.value.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
});

const uniquePlayers = computed(() => {
  const playerIds = new Set(uptimeData.value.map(record => record.playerId));
  return playerIds.size;
});

const onlineCount = computed(() => uptimeData.value.filter(r => r.status === 'online').length);
const offlineCount = computed(() => uptimeData.value.filter(r => r.status === 'offline').length);

const formatDateRange = () => {
  if (!startDate.value || !endDate.value) return '';
  return `${new Date(startDate.value).toLocaleDateString()} - ${new Date(endDate.value).toLocaleDateString()}`;
};

const fetchUptimeData = async () => {
  if (!startDate.value || !endDate.value) {
    error.value = 'Please select both start and end dates';
    return;
  }

  isLoading.value = true;
  error.value = '';

  try {
    // Calculate startDate - 1 day and endDate + 1 day for fetching
    const start = new Date(startDate.value);
    start.setDate(start.getDate() - 1);
    const fetchStartDate = start.toISOString().split('T')[0];

    const end = new Date(endDate.value);
    end.setDate(end.getDate() + 1);
    const fetchEndDate = end.toISOString().split('T')[0];

    const data = await getPlayerUptimes(fetchStartDate, fetchEndDate);
    uptimeData.value = data;
  } catch (err: any) {
    console.error('Error fetching uptime data:', err);
    error.value = err?.response?.data?.message || 'Failed to fetch uptime data. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  setDefaultDateRange();
  fetchUptimeData();
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* ---- Page surface (per-view root supplies its own padding; PageLayout paints
   the magenta canvas edge-to-edge for `.bui-entity-listing-page`). ---- */
.uptime-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
  @media (max-width: 768px) {
    padding-left: 8px;
    padding-right: 8px;
  }
}

.uptime-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

/* ---- Stat tile row (mirrors PlayerEditorView `.pe-stats`) ---- */
.pe-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

/* ---- Date-range controls inside the card header (white surface) ---- */
.uptime-dates {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.uptime-date {
  display: inline-flex;
  flex-direction: column;
  gap: 0.25rem;
}

.uptime-date__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bui-tbl-detail-text, #5c5c66);
}

.uptime-date__input {
  font-family: inherit;
  font-size: 0.85rem;
  color: var(--bui-tbl-row-text, #2a2969);
  background: var(--bui-tbl-row-fill, #fffcfd);
  border: 1px solid var(--bui-tbl-line, #ffd9e6);
  border-radius: 8px;
  padding: 0.4rem 0.6rem;

  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 1px;
  }
}

/* ---- Chart / state panel ---- */
.uptime-chart-panel {
  padding: 1rem 1.25rem 1.25rem;
  min-height: 440px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.uptime-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  color: var(--bui-tbl-detail-text, #5c5c66);
  text-align: center;
}

.uptime-state__message {
  color: var(--bui-badge-bad-text, #b91c1c);
  font-weight: 600;
  margin: 0;
}

.uptime-retry {
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  border-radius: 8px;
  padding: 0.5rem 1.1rem;
  color: var(--bui-cta-pri-text, #2a2969);
  background: var(--bui-cta-pri-fill, #baff5c);

  &:hover {
    filter: brightness(0.96);
  }
  &:focus-visible {
    outline: 2px solid var(--bui-chk-checked-fill, #de3da9);
    outline-offset: 2px;
  }
}
</style>
